/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    Injectable,
    OnDestroy
} from '@angular/core';
import {
    Subject,
    BehaviorSubject,
    fromEvent
} from 'rxjs';
import {
    takeUntil,
    debounceTime
} from 'rxjs/operators';
import * as _ from 'lodash';

/**
 * Screen Breakpoints Enum.
 */
export enum BreakpointEnum {
    xs= 'xs',
    sm= 'sm',
    md= 'md',
    lg= 'lg',
    xl= 'xl',
    xxl= 'xxl'
}
/**
 * FC AG Responsive Service.
 */
@Injectable({
    providedIn: 'root'
})
export class FcAgResponsiveService implements OnDestroy {
    private readonly DEFAULT_MOBILE_MODE_BREAKPOINT = 435; // in px

    private _mobileModeBreakpoint: number | undefined; // in px
    
    /**
     * Getter the width of breakpoint where switching Desktop / Mobile.
     *
     * @returns Number.
     */
    get mobileModeBreakpoint(): number { return this._mobileModeBreakpoint ?? this.DEFAULT_MOBILE_MODE_BREAKPOINT; }

    /**
     * Setter the width of breakpoint or set default.
     *
     * @param _width Number.
     */
    set mobileModeBreakpoint(_width: number) { 
        this._mobileModeBreakpoint = (!_.isNil(_width) && _.isNumber(_width))
            ? Math.abs(_width)
            : this.DEFAULT_MOBILE_MODE_BREAKPOINT; 
    }
    
    private _unsubscribeAll$: Subject<any> = new Subject();

    private _screenWidth$: BehaviorSubject<number> = new BehaviorSubject(-1);

    private _mediaBreakpoint$: BehaviorSubject<string> = new BehaviorSubject('');
    
    /**
     * Getter mediaBreakpoint as observable subject.
     *
     * @returns BehaviorSubject.
     */
    get mediaBreakpoint$(): BehaviorSubject<string> { return this._mediaBreakpoint$; }

    /**
     * Getter JIT mediaBreakpoint as string.
     * 
     * @returns String.
     */
    get mediaBreakpoint(): string { return this._mediaBreakpoint$.getValue(); }

    /**
     * Getter screenWith as observable subject.
     *
     * @returns BehaviorSubject.
     */
    get screenWith$(): BehaviorSubject<number> { return this._screenWidth$; }

    /**
     * Getter JIT screenWidth as number.
     *
     * @returns Number.
     */
    get screenWith(): number { return this._screenWidth$.getValue(); }

    /**
     * Service Constructor.
     */
    constructor() {
        this._setScreenWidth(window.innerWidth);
        this._setMediaBreakpoint(window.innerWidth);
        fromEvent(window, 'resize')
            .pipe(
                debounceTime(250),
                takeUntil(this._unsubscribeAll$)
            ).subscribe((evt: any) => {
                this._setScreenWidth(evt.target.innerWidth);
                this._setMediaBreakpoint(evt.target.innerWidth);
            });
    }

    private _setScreenWidth(width: number): void {
        this._screenWidth$.next(width);
    }

    private _setMediaBreakpoint(width: number): void {
        if (width < 576) {
            this._mediaBreakpoint$.next(BreakpointEnum.xs);
        } else if (width >= 576 && width < 768) {
            this._mediaBreakpoint$.next(BreakpointEnum.sm);
        } else if (width >= 768 && width < 992) {
            this._mediaBreakpoint$.next(BreakpointEnum.md);
        } else if (width >= 992 && width < 1200) {
            this._mediaBreakpoint$.next(BreakpointEnum.lg);
        } else if (width >= 1200 && width < 1600) {
            this._mediaBreakpoint$.next(BreakpointEnum.xl);
        } else {
            this._mediaBreakpoint$.next(BreakpointEnum.xxl);
        }
    }
    
    /**
     * Check if current screen is Mobile or Desktop.
     *
     * @returns Boolean.
     */
    public isMobileMode(): boolean {
        return this.screenWith < this.mobileModeBreakpoint;
    }

    /**
     * Service Destroying.
     */
    ngOnDestroy() {
        this._unsubscribeAll$.next();
        this._unsubscribeAll$.complete();
    }
    
}